import { settingService } from '@/services/settingService';
import { useQuery } from 'react-query';

type RawSetting = {
  code?: string;
  key?: string;
  keyword?: string;
  value?: string | number;
};

export type SettingItem = {
  code: string;
  keyword?: string;
  value?: string;
};

const normalizeSettings = (payload: unknown): SettingItem[] => {
  const normalizeItem = (item: RawSetting): SettingItem | null => {
    const code = item.code ?? item.key;

    if (!code) {
      return null;
    }

    return {
      code,
      keyword: item.keyword,
      value:
        item.value === undefined || item.value === null
          ? undefined
          : String(item.value),
    };
  };

  if (Array.isArray(payload)) {
    return payload
      .map(item => normalizeItem(item as RawSetting))
      .filter((item): item is SettingItem => item !== null);
  }

  if (payload && typeof payload === 'object') {
    return Object.entries(payload as Record<string, unknown>).map(([code, value]) => ({
      code,
      value: value === undefined || value === null ? undefined : String(value),
    }));
  }

  return [];
};

export default function useSettings(code: string) {
  const query = useQuery(
    ['all-settings'],
    async () => {
      const response = await settingService.getAllSettings();
      return normalizeSettings(response?.data);
    },
    {
      enabled: Boolean(code),
      staleTime: 1000 * 60 * 10,
    },
  );

  const setting =
    query.data?.find(item => item.code === code || item.keyword === code) ?? null;
  return {
    ...query,
    setting,
    value: setting?.value,
    keyword: setting?.keyword,
  };
}
